// Karte „Kundenverbindungen" ganz unten im Dashboard (User 28.09.2026) —
// ersetzt den Knopf „Kundenverbindungen anzeigen" der Kontakttabelle. Listet
// alle Kundenverbindungen; Klick öffnet das Bearbeiten-Popup
// (`redesign/KundenverbindungDialog.tsx`).
import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { People20Regular } from "@fluentui/react-icons";
import { apiFetch } from "../../utils/apiFetch";
import { REFINED } from "../risikoanalyseStep/tokens";
import { DashCard, EmptyHint } from "./DashboardShared";
import { KundenverbindungDialog, type KundenverbindungKurz } from "../redesign/KundenverbindungDialog";

export const KundenverbindungenCard: React.FC = () => {
    const [liste, setListe] = useState<KundenverbindungKurz[] | null>(null);
    const [fehler, setFehler] = useState(false);
    const [suche, setSuche] = useState("");
    const [offen, setOffen] = useState<KundenverbindungKurz | null>(null);
    const [searchParams] = useSearchParams();
    const karteRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        let abgebrochen = false;
        apiFetch<KundenverbindungKurz[]>("contactgroup/kundenverbindungen")
            .then(({ data }) => {
                if (!abgebrochen) setListe(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (!abgebrochen) {
                    setListe([]);
                    setFehler(true);
                }
            });
        return () => {
            abgebrochen = true;
        };
    }, []);

    // Alte Links auf die frühere Tabellen-Ansicht landen hier (`/?kv=1`).
    useEffect(() => {
        if (searchParams.get("kv") === "1" && liste) {
            karteRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [searchParams, liste]);

    const gefiltert = useMemo(() => {
        const q = suche.trim().toLowerCase();
        const alle = [...(liste ?? [])].sort((a, b) => a.name.localeCompare(b.name, "de", { sensitivity: "base" }));
        return q ? alle.filter((g) => g.name.toLowerCase().includes(q)) : alle;
    }, [liste, suche]);

    return (
        <div ref={karteRef}>
            <DashCard
                title="Kundenverbindungen"
                icon={<People20Regular />}
                afterTitle={
                    liste ? (
                        <span style={{ fontFamily: REFINED.fontUI, fontSize: "0.85rem", fontWeight: 700, color: REFINED.ink3 }}>
                            {liste.length}
                        </span>
                    ) : null
                }
                right={
                    <input
                        type="search"
                        value={suche}
                        onChange={(e) => setSuche(e.target.value)}
                        placeholder="Kundenverbindung suchen …"
                        aria-label="Kundenverbindung suchen"
                        style={{
                            fontFamily: REFINED.fontUI,
                            fontSize: "0.88rem",
                            padding: "7px 12px",
                            borderRadius: 9,
                            border: `1.5px solid ${REFINED.line}`,
                            minWidth: 0,
                            width: 240,
                            maxWidth: "100%",
                        }}
                    />
                }
            >
                {liste === null ? (
                    <EmptyHint>Lädt …</EmptyHint>
                ) : fehler ? (
                    <EmptyHint>Kundenverbindungen konnten nicht geladen werden.</EmptyHint>
                ) : gefiltert.length === 0 ? (
                    <EmptyHint>{suche.trim() ? "Keine Kundenverbindung gefunden." : "Keine Kundenverbindungen vorhanden."}</EmptyHint>
                ) : (
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                            gap: 8,
                            maxHeight: 420,
                            overflowY: "auto",
                            paddingTop: 8,
                        }}
                    >
                        {gefiltert.map((g) => (
                            <button
                                key={g.id}
                                type="button"
                                onClick={() => setOffen(g)}
                                title="Bearbeiten"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    textAlign: "left",
                                    padding: "10px 12px",
                                    borderRadius: 10,
                                    border: `1px solid ${REFINED.line}`,
                                    background: REFINED.white,
                                    cursor: "pointer",
                                    fontFamily: REFINED.fontUI,
                                    minWidth: 0,
                                }}
                            >
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span
                                        style={{
                                            display: "block",
                                            fontWeight: 700,
                                            fontSize: "0.92rem",
                                            color: REFINED.ink,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {g.name}
                                    </span>
                                    <span style={{ display: "block", fontSize: "0.78rem", color: REFINED.ink3, marginTop: 2 }}>
                                        {g.ansprechpartnerName ? `Verantw.: ${g.ansprechpartnerName}` : "ohne Verantwortlichen"}
                                    </span>
                                </span>
                                <span
                                    style={{
                                        flex: "0 0 auto",
                                        fontSize: "0.78rem",
                                        fontWeight: 700,
                                        color: REFINED.aub,
                                        background: "#f8eef5",
                                        borderRadius: 999,
                                        padding: "2px 9px",
                                    }}
                                >
                                    {g.memberCount}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </DashCard>
            {offen && (
                <KundenverbindungDialog
                    kv={offen}
                    alle={liste ?? []}
                    onClose={() => setOffen(null)}
                    onGeaendert={(neu) =>
                        setListe((alt) =>
                            (alt ?? [])
                                .map((x) => (neu && x.id === neu.id ? { ...x, ...neu } : x))
                                .filter((x) => neu || x.id !== offen.id),
                        )
                    }
                />
            )}
        </div>
    );
};
